import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Eye,
  EyeOff,
  Mic,
  Plus,
  Printer,
  Search,
  Trash2,
} from "lucide-react";
import { Button } from "@/v5/components/ui/button";
import { Input } from "@/v5/components/ui/input";
import { Checkbox } from "@/v5/components/ui/checkbox";
import { NoteViewModal } from "@/v5/components/NoteViewModal";
import { NoteDetailPanel } from "@/v5/components/NoteDetailPanel";
import { DetailRightPanel } from "@/v5/components/DetailRightPanel";
import { PrintOptionsDialog } from "@/v5/components/PrintOptionsDialog";
import { DataTable } from "@/components/ui/DataTable";
import type { DataTableColumn } from "@/components/ui/DataTable";
import type { PatientNote } from "@/v5/services/notes.service";
import type { PatientSummary } from "@/types/patient.types";
import * as notesSvc from "@/v5/services/notes.service";
import { useNoteForm } from "@/v5/contexts/NoteFormContext";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { formatNoteDateTime, sortNotes } from "@/v5/lib/notesTable";
import { toast } from "@/store/toastStore";

interface NotesSectionProps {
  patientId: string;
  /** Passed through to the note views so "Add Task" can prefill the patient. */
  patientSummary?: PatientSummary | null;
}

export function NotesSection({ patientId, patientSummary }: NotesSectionProps) {
  const [notes, setNotes] = useState<PatientNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // Defaults to the order /notes already returns (ORDER BY noteDate DESC), so
  // turning sorting on did not change the list anyone was already looking at.
  const [sortKey, setSortKey] = useState<string>("noteDate");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Side-panel selection (replaces the old full-screen viewNoteId modal flow).
  // selectedNoteId opens the right-side detail panel; noteViewExpanded opens the
  // full-screen NoteViewModal when the panel's "Open" button is clicked.
  const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null);
  const [panelExpandTrigger, setPanelExpandTrigger] = useState(0);
  const [noteViewExpanded, setNoteViewExpanded] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  // Bumped whenever the selected note is mutated outside the detail panel
  // (expanded modal, edit form) so NoteDetailPanel refetches and never shows
  // stale state after the modal/form closes.
  const [detailRefreshTrigger, setDetailRefreshTrigger] = useState(0);
  const refreshDetail = useCallback(() => setDetailRefreshTrigger((t) => t + 1), []);

  const selectNote = useCallback((noteId: number) => {
    setSelectedNoteId(noteId);
    setPanelExpandTrigger((t) => t + 1);
  }, []);

  // The note edit/create modal is owned by FloatingNoteForm at the Layout
  // level so it survives route changes. We just open it via the context and
  // refresh our list whenever a note for THIS patient closes.
  const noteForm = useNoteForm();
  const hadOurNoteRef = useRef(false);

  const prevPatientRef = useRef(patientId);
  useEffect(() => {
    if (prevPatientRef.current !== patientId) {
      prevPatientRef.current = patientId;
      setNotes([]);
      setSelectedIds(new Set());
      setSelectedNoteId(null);
      setNoteViewExpanded(false);
      setSearchTerm("");
      setSortKey("noteDate");
      setSortDir("desc");
    }
  }, [patientId]);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await notesSvc.listNotes(patientId, true);
      setNotes(data);
    } catch {
      toast.error("Failed to load notes");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    void fetchNotes();
  }, [fetchNotes]);

  const filteredNotes = useMemo(() => {
    let result = notes;
    if (!showDeleted) {
      result = result.filter((n) => n.active !== 0);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (n) =>
          n.title.toLowerCase().includes(q) ||
          String(n.noteId).includes(q) ||
          n.typeName.toLowerCase().includes(q) ||
          n.authorName.toLowerCase().includes(q),
      );
    }
    return result;
  }, [notes, showDeleted, searchTerm]);

  const sortedNotes = useMemo(
    () => sortNotes(filteredNotes, sortKey, sortDir),
    [filteredNotes, sortKey, sortDir],
  );

  const handleSortChange = useCallback((key: string, direction: "asc" | "desc") => {
    setSortKey(key);
    setSortDir(direction);
  }, []);

  // The empty state has to say WHY the table is empty. A search that matched
  // nothing is not a patient without notes, and neither is a patient whose
  // notes are all deleted while "Show deleted" is off.
  const emptyState = useMemo(() => {
    const q = searchTerm.trim();
    if (q) {
      return {
        title: "No matching notes",
        description: `No notes match "${q}". Try a different search.`,
      };
    }
    if (!showDeleted && notes.length > 0) {
      return {
        title: "No active notes",
        description: 'Every note for this patient is deleted. Turn on "Show deleted" to see them.',
      };
    }
    return {
      title: "No notes found",
      description: "This patient has no notes yet.",
    };
  }, [searchTerm, showDeleted, notes.length]);


  async function handleBulkDelete() {
    if (selectedIds.size === 0) { toast.error("No notes selected."); return; }
    if (!window.confirm(`Delete ${selectedIds.size} selected note(s)?`)) return;
    try {
      const ids = Array.from(selectedIds).join(",");
      await notesSvc.deleteNote(patientId, ids);
      toast.success("Notes deleted");
      setSelectedIds(new Set());
      void fetchNotes();
    } catch {
      toast.error("Failed to delete notes");
    }
  }

  function handlePrintSelected() {
    if (selectedIds.size === 0) { toast.error("No notes selected."); return; }
    setPrintOpen(true);
  }

  function handleNewNote() {
    noteForm.openNote({ patientId, noteId: null });
  }

  function handleEditNote(noteId: number) {
    noteForm.openNote({ patientId, noteId });
  }

  function handleExpandedViewClose() {
    setNoteViewExpanded(false);
    void fetchNotes();
  }

  function handlePanelClose() {
    setSelectedNoteId(null);
    setNoteViewExpanded(false);
  }

  // Refresh the list whenever a note that was open for this patient closes
  // (saved or discarded). Mirrors the old handleFormClose behavior without
  // needing the modal to live inside this section.
  useEffect(() => {
    const isOurs =
      noteForm.current !== null && noteForm.current.patientId === patientId;
    if (isOurs) {
      hadOurNoteRef.current = true;
    } else if (hadOurNoteRef.current) {
      hadOurNoteRef.current = false;
      void fetchNotes();
      // An edit form for this patient just closed — the open detail panel may
      // be showing the note we just edited, so force it to refetch too.
      refreshDetail();
    }
  }, [noteForm.current, patientId, fetchNotes, refreshDetail]);

  const userTz = useUserTimezone();

  const notesColumns = useMemo<DataTableColumn<PatientNote>[]>(() => [
    {
      key: 'noteId',
      header: 'ID#',
      width: 'w-20',
      sortable: true,
      render: (row) => <span className="text-xs text-slate-500">{row.noteId}</span>,
    },
    {
      // `w-32` (was `w-16`): the cell now carries "Aug 27, 2026" over
      // "3:04 PM PDT", and under `tableFixed` a 4rem column would clip both.
      key: 'noteDate',
      header: 'Date',
      width: 'w-32',
      sortable: true,
      render: (row) => {
        if (!row.noteDate) return <span className="text-xs text-slate-400">&mdash;</span>;
        const { date, time } = formatNoteDateTime(row.noteDate, userTz);
        return (
          <span className="flex flex-col items-start leading-tight whitespace-nowrap">
            <span className="text-xs">{date}</span>
            {time && <span className="text-xs text-slate-400">{time}</span>}
          </span>
        );
      },
    },
    {
      key: 'typeName',
      header: 'Type',
      width: 'w-44',
      sortable: true,
      render: (row) => <span className="text-xs break-words">{row.typeName}</span>,
    },
    {
      key: 'authorName',
      header: 'By',
      width: 'w-32',
      sortable: true,
      render: (row) => <span className="text-xs text-slate-500 break-words">{row.authorName}</span>,
    },
    {
      key: 'title',
      header: 'Subject',
      truncate: true,
      sortable: true,
      render: (row) => <span className="text-xs" title={row.title}>{row.title}</span>,
    },
    {
      key: 'visibleToPatient',
      header: 'Visible',
      width: 'w-16',
      sortable: true,
      render: (row) => row.visibleToPatient
        ? <span className="font-semibold text-green-600 flex items-center gap-1 text-xs"><Eye className="h-3 w-3" /> Yes</span>
        : <span className="text-slate-400 flex items-center gap-1 text-xs"><EyeOff className="h-3 w-3" /> No</span>,
    },
  ], [userTz]);

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 bg-white">
      {/* Left column: list (toolbar + table) */}
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
        {/* Toolbar (SCRUM-1443)
            Three nested flex rows, none of which wrapped, plus a fixed `w-48` search — so
            the row's min-content width was past 375px, `justify-between` pushed the last
            item off, and "+ New Note" rendered as "+ Ne". Each row gets its wrap escape and
            the search gets its full width back below `md`; the search leads on a phone
            because it is the control the row is for. */}
        <div className="flex items-center justify-between gap-3 px-4 py-2 flex-shrink-0 max-md:flex-wrap-reverse">
        <div className="flex items-center gap-3 max-md:w-full max-md:flex-wrap">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
            <Checkbox checked={showDeleted} onCheckedChange={(v) => setShowDeleted(!!v)} />
            Show deleted
          </label>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-1.5">
              <Button variant="outline" size="sm" onClick={handlePrintSelected} className="gap-1">
                <Printer className="h-3.5 w-3.5" /> Print ({selectedIds.size})
              </Button>
              <Button variant="outline" size="sm" onClick={handleBulkDelete} className="gap-1 text-destructive">
                <Trash2 className="h-3.5 w-3.5" /> Delete ({selectedIds.size})
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 max-md:w-full max-md:flex-wrap">
          <div className="relative max-md:w-full">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search notes..." className="pl-8 h-8 w-48 text-xs max-md:w-full" />
          </div>
          <Button variant="outline" size="sm" onClick={handlePrintSelected} title="Print selected"><Printer className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => noteForm.openNote({ patientId, noteId: null, autoRecord: true })} title="New Recording"><Mic className="h-4 w-4" /></Button>
          <Button size="sm" onClick={handleNewNote} className="gap-1"><Plus className="h-4 w-4" /> New Note</Button>
        </div>
      </div>

      {/* Table. `max-md:pb-16` clears the browser's bottom chrome (SCRUM-1443): below `md`
          the shell releases the viewport lock and the document is the scroller, so the last
          row ends up behind the URL bar. `pb-safe` cannot do this — index.html carries no
          `viewport-fit=cover`, so `env(safe-area-inset-bottom)` is 0px, and browser chrome
          is not a safe-area inset in any case. */}
      <div className="min-h-0 flex-1 max-md:pb-16">
        <DataTable<PatientNote>
          data={sortedNotes}
          columns={notesColumns}
          keyField="noteId"
          tableFixed
          loading={loading}
          selectable
          selectedIds={selectedIds as Set<string | number>}
          onSelectionChange={(ids) => setSelectedIds(ids as Set<number>)}
          onRowClick={(row) => selectNote(row.noteId)}
          rowClassName={(row) => row.active === 0 ? 'opacity-50' : undefined}
          manualSort
          sortKey={sortKey}
          sortDir={sortDir}
          onSortChange={handleSortChange}
          page={1}
          pageSize={sortedNotes.length || 1}
          total={sortedNotes.length}
          onPageChange={() => {}}
          emptyTitle={emptyState.title}
          emptyDescription={emptyState.description}
        />
      </div>

        {printOpen && <PrintOptionsDialog patientId={patientId} noteIds={sortedNotes.filter(n => selectedIds.has(n.noteId)).map(n => n.noteId).join(",")} onClose={() => setPrintOpen(false)} />}
      </div>

      {/* Right column: note detail panel — matches the Labs preview right
          sidebar width (DetailRightPanel = w-[clamp(28rem,50vw,52rem)] +
          collapse chevron). */}
      {selectedNoteId !== null && (
        <DetailRightPanel width="w-[clamp(28rem,50vw,52rem)]" expandTrigger={panelExpandTrigger}>
          <NoteDetailPanel
            patientId={patientId}
            noteId={selectedNoteId}
            patientSummary={patientSummary}
            onExpand={() => setNoteViewExpanded(true)}
            onClose={handlePanelClose}
            onEdit={(id) => {
              setNoteViewExpanded(false);
              handleEditNote(id);
            }}
            onMutated={() => void fetchNotes()}
            refreshTrigger={detailRefreshTrigger}
          />
        </DetailRightPanel>
      )}

      {/* Full-screen expanded view (opened by the panel's "Open" button) */}
      {noteViewExpanded && selectedNoteId !== null && (
        <NoteViewModal
          patientId={patientId}
          noteId={selectedNoteId}
          patientSummary={patientSummary}
          onClose={handleExpandedViewClose}
          onEdit={(id) => {
            setNoteViewExpanded(false);
            handleEditNote(id);
          }}
          onMutated={() => {
            void fetchNotes();
            refreshDetail();
          }}
        />
      )}
    </div>
  );
}
